/**
 * Générateur — Automatisme : connaître la somme des angles d'un triangle,
 * calculer le 3e angle d'un triangle connaissant les mesures des deux autres.
 * Attendu ID : 1077 | Niveau : 5ème | Domaine : Triangles
 * Rubrique : AUTOMATISME (programme 2025) — mobilisation en rituel court.
 * Labels (cartes exerciseur) :
 *   - Le troisième angle                 (saisie entière, figure aux vrais angles)
 *   - Possible ou pas ?                  (V/F raisonnement, lignes recalculées)
 *   (format « Quelle nature, d'après ses angles ? » retiré à la revue du 2026-09-02, décision prof)
 *
 * ── Plan de cartes validé (2026-09-02) ─────────────────────────────────────
 * Source : Automatisme n°39 du doc 5e + grille P1-P12 (analyse BO cycles 3-4).
 * APPUI 6e : « connaître la valeur de la somme des mesures des angles d'un
 * triangle et l'utiliser pour calculer des angles » ; propriétés angulaires
 * des triangles particuliers. LIMITE (fiche) : DÉMONTRER la propriété est un
 * objectif de 5e — l'automatisme porte sur son USAGE en calcul.
 * P3 : mesures ENTIÈRES en degrés. P6/P10 : on CALCULE, on ne mesure pas
 * (aucun rapporteur, pas de calculatrice — une ou deux soustractions).
 * OBSTACLES P11 (fiche) : deux angles obtus impossibles (question d.), une
 * somme qui dépasserait 180° (flash Q5), l'angle droit oublié (Q2, Q7).
 * INVARIANTS :
 *   - tout triangle est RÉALISABLE : somme 180, chaque angle ≥ 20° ; la
 *     figure SVG est CONSTRUITE aux vrais angles (loi des sinus), penchée ;
 *   - réponses de saisie ENTIÈRES ; isocèle « sommet donné » n'est tiré
 *     qu'avec un sommet pair ((180 − a) ÷ 2 entier) ;
 *   - pool conceptuel du V/F avec interdiction stricte de reprise ;
 *   - data-angles dans le SVG pour les solveurs ; pas de calculatrice.
 */
import {
    randomChoice, randInt, shuffle,
    makeVraiFaux, makeSaisie,
} from '../../../utils';
import { imgElement } from '../statistiques/svgStats';

const ATTENDU_ID = 1077;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_TROISIEME = 'Le troisième angle';
const L_POSSIBLE = 'Possible ou pas ?';

const NOMS = ['A', 'B', 'C'];

// ─── Géométrie : un triangle à partir de ses trois angles ──────────────────

const verifierAngles = (angles) => {
    if (angles.length !== 3 || angles.some((a) => !Number.isInteger(a) || a < 20)) throw new Error('gen_1077 : angle non entier ou < 20°');
    if (angles[0] + angles[1] + angles[2] !== 180) throw new Error('gen_1077 : la somme des angles n\'est pas 180°');
};

/** Sommets A, B, C d'un triangle d'angles (α, β, γ) en degrés, base AB = 5. */
const construireTriangle = (angles) => {
    const rad = (d) => (d * Math.PI) / 180;
    const [al, be, ga] = angles;
    const c = 5;
    const b = (c * Math.sin(rad(be))) / Math.sin(rad(ga)); // AC
    const C = [b * Math.cos(rad(al)), b * Math.sin(rad(al))];
    return [[0, 0], [c, 0], C];
};

const tourner = (pts, deg) => {
    const a = (deg * Math.PI) / 180; const co = Math.cos(a); const si = Math.sin(a);
    return pts.map(([x, y]) => [x * co - y * si, x * si + y * co]);
};

const norme = (u) => Math.hypot(u[0], u[1]);

/**
 * Rendu : triangle, arcs et étiquettes des angles connus, « ? » à l'inconnu,
 * petit carré si angle droit codé, traits d'égalité si isocèle codé.
 * spec = { angles, connus: [i…], droit: i|null, egaux: [cote, cote]|null }
 */
const svgTriangle = (pts, spec) => {
    const w = 320; const h = 240; const marge = 36;
    const xs = pts.map((p) => p[0]); const ys = pts.map((p) => p[1]);
    const xmin = Math.min(...xs); const xmax = Math.max(...xs);
    const ymin = Math.min(...ys); const ymax = Math.max(...ys);
    const ech = Math.min((w - 2 * marge) / (xmax - xmin), (h - 2 * marge) / (ymax - ymin));
    const X = (x) => marge + (x - xmin) * ech + ((w - 2 * marge) - (xmax - xmin) * ech) / 2;
    const Y = (y) => marge + (ymax - y) * ech + ((h - 2 * marge) - (ymax - ymin) * ech) / 2;
    const P = pts.map((p) => [X(p[0]), Y(p[1])]);
    const cx = (P[0][0] + P[1][0] + P[2][0]) / 3; const cy = (P[0][1] + P[1][1] + P[2][1]) / 3;

    let s = `<path d="M ${P[0][0].toFixed(1)} ${P[0][1].toFixed(1)} L ${P[1][0].toFixed(1)} ${P[1][1].toFixed(1)} L ${P[2][0].toFixed(1)} ${P[2][1].toFixed(1)} Z" fill="#eff6ff" stroke="#1E3A8A" stroke-width="2.2"/>`;
    for (let v = 0; v < 3; v++) {
        const S = P[v]; const U = P[(v + 1) % 3]; const Vp = P[(v + 2) % 3];
        const u1 = [(U[0] - S[0]) / norme([U[0] - S[0], U[1] - S[1]]), (U[1] - S[1]) / norme([U[0] - S[0], U[1] - S[1]])];
        const u2 = [(Vp[0] - S[0]) / norme([Vp[0] - S[0], Vp[1] - S[1]]), (Vp[1] - S[1]) / norme([Vp[0] - S[0], Vp[1] - S[1]])];
        const bis = [u1[0] + u2[0], u1[1] + u2[1]]; const nb = norme(bis) || 1;
        const bx = bis[0] / nb; const by = bis[1] / nb;
        if (spec.droit === v) {
            const k = 11;
            s += `<polyline points="${(S[0] + u1[0] * k).toFixed(1)},${(S[1] + u1[1] * k).toFixed(1)} ${(S[0] + (u1[0] + u2[0]) * k).toFixed(1)},${(S[1] + (u1[1] + u2[1]) * k).toFixed(1)} ${(S[0] + u2[0] * k).toFixed(1)},${(S[1] + u2[1] * k).toFixed(1)}" fill="none" stroke="#111827" stroke-width="1.8" data-code="carre" data-sommet="${v}"/>`;
        } else {
            // Arc échantillonné à l'intérieur de l'angle (jamais d'ambiguïté de balayage).
            const r = 16;
            const a1 = Math.atan2(u1[1], u1[0]); let a2 = Math.atan2(u2[1], u2[0]);
            let d = a2 - a1; while (d > Math.PI) d -= 2 * Math.PI; while (d < -Math.PI) d += 2 * Math.PI;
            const pas = 10; let pathArc = '';
            for (let t = 0; t <= pas; t++) {
                const ang = a1 + (d * t) / pas;
                pathArc += `${t === 0 ? 'M' : 'L'} ${(S[0] + r * Math.cos(ang)).toFixed(1)} ${(S[1] + r * Math.sin(ang)).toFixed(1)} `;
            }
            s += `<path d="${pathArc}" fill="none" stroke="#B45309" stroke-width="1.6"/>`;
            const connu = spec.connus.includes(v);
            // Recul ADAPTATIF (revue 2026-09-02, A6) : à 34 px, un angle étroit
            // (< 36°) n'offre plus la largeur d'un « 25° » — on recule jusqu'à ce
            // que le demi-secteur fasse 11 px, sans dépasser 60 % de la bissectrice.
            const theta = Math.acos(Math.max(-1, Math.min(1, u1[0] * u2[0] + u1[1] * u2[1])));
            const longueurBis = (() => {
                // intersection de S + t·b avec la droite (U, Vp)
                const ex = Vp[0] - U[0]; const ey = Vp[1] - U[1];
                const den = bx * ey - by * ex;
                return Math.abs(den) < 1e-9 ? 60 : ((U[0] - S[0]) * ey - (U[1] - S[1]) * ex) / den;
            })();
            const recul = Math.min(Math.max(34, 11 / Math.tan(theta / 2) + 6), 0.6 * longueurBis);
            const lx = S[0] + bx * recul; const ly = S[1] + by * recul + 4;
            s += `<text x="${lx.toFixed(1)}" y="${ly.toFixed(1)}" font-size="12.5" font-weight="700" fill="${connu ? '#B45309' : '#B45309'}" text-anchor="middle">${connu ? `${spec.angles[v]}°` : '?'}</text>`;
        }
        // étiquette du sommet, vers l'extérieur
        const dx = S[0] - cx; const dy = S[1] - cy; const L = norme([dx, dy]) || 1;
        s += `<text x="${(S[0] + (dx / L) * 16).toFixed(1)}" y="${(S[1] + (dy / L) * 16 + 5).toFixed(1)}" font-size="13" font-weight="700" fill="#1e293b" text-anchor="middle">${NOMS[v]}</text>`;
    }
    if (spec.egaux) {
        spec.egaux.forEach((i) => {
            const A = P[i]; const B = P[(i + 1) % 3];
            const mx = (A[0] + B[0]) / 2; const my = (A[1] + B[1]) / 2;
            const dd = [B[0] - A[0], B[1] - A[1]]; const L = norme(dd);
            const nx = -dd[1] / L; const ny = dd[0] / L;
            s += `<line x1="${(mx - nx * 6).toFixed(1)}" y1="${(my - ny * 6).toFixed(1)}" x2="${(mx + nx * 6).toFixed(1)}" y2="${(my + ny * 6).toFixed(1)}" stroke="#111827" stroke-width="2" data-code="tick" data-cote="${i}"/>`;
        });
    }
    const dataAngles = spec.angles.map((a, i) => `${NOMS[i]}:${a}`).join('|');
    const dataConnus = spec.connus.map((i) => NOMS[i]).join('');
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}" width="${w}" height="${h}" font-family="Arial" data-angles="${dataAngles}" data-connus="${dataConnus}" data-droit="${spec.droit === null ? '' : NOMS[spec.droit]}">${s}</svg>`;
};

// ─── Format 1 : le troisième angle (saisie) ────────────────────────────────

/** Tire un triangle typé et l'angle inconnu ; angles en position A, B, C. */
const tirerTriangleTypé = () => {
    const type = randomChoice(['quelconque', 'rectangle', 'isocele_sommet', 'isocele_base']);
    let angles; let egauxCotes = null; let droit = null; let inconnu; let connus;
    if (type === 'quelconque') {
        for (;;) {
            const a = randInt(25, 110); const b = randInt(25, 110); const c = 180 - a - b;
            if (c < 20 || a === b || b === c || a === c || [a, b, c].includes(90)) continue;
            angles = [a, b, c]; break;
        }
        inconnu = randInt(0, 2);
        connus = [0, 1, 2].filter((i) => i !== inconnu);
    } else if (type === 'rectangle') {
        const b = randInt(20, 70);
        angles = [90, b, 90 - b];
        droit = 0;
        inconnu = randomChoice([1, 2]);
        connus = [1, 2].filter((i) => i !== inconnu); // l'angle droit est CODÉ, pas écrit
    } else if (type === 'isocele_sommet') {
        const a = 2 * randInt(10, 70); // sommet pair, 20..140 → base ≥ 20
        angles = [a, (180 - a) / 2, (180 - a) / 2];
        egauxCotes = [0, 2]; // AB = CA : sommet principal A
        inconnu = randomChoice([1, 2]);
        connus = [0];
    } else {
        const b = randInt(20, 80);
        angles = [180 - 2 * b, b, b];
        egauxCotes = [0, 2];
        inconnu = 0;
        connus = [randomChoice([1, 2])];
    }
    verifierAngles(angles);
    // Décalage des lettres : le triangle est réétiqueté au hasard.
    const s = randInt(0, 2);
    const perm = (i) => (i + s) % 3;
    const anglesD = [0, 1, 2].map((i) => angles[(i - s + 3) % 3]);
    return {
        type, angles: anglesD,
        inconnu: perm(inconnu), connus: connus.map(perm),
        droit: droit === null ? null : perm(droit),
        egauxCotes: egauxCotes ? egauxCotes.map(perm) : null,
    };
};

let derniereSignatureTroisieme1077 = null;

const genTroisieme = () => {
    let payload = construireTroisieme();
    for (let t = 0; t < 8 && payload.texte === derniereSignatureTroisieme1077; t++) payload = construireTroisieme();
    derniereSignatureTroisieme1077 = payload.texte;
    return payload;
};

const construireTroisieme = () => {
    const T = tirerTriangleTypé();
    const pts = tourner(construireTriangle(T.angles), Math.random() < 0.3 ? 0 : randInt(5, 355));
    const reponse = T.angles[T.inconnu];
    verifierAngles(T.angles);
    const svg = svgTriangle(pts, { angles: T.angles, connus: T.connus, droit: T.droit, egaux: T.egauxCotes });
    const donnees = T.connus.map((i) => `l'angle en ${NOMS[i]} mesure ${T.angles[i]}°`).join(' et ');
    let contexte = '';
    if (T.type === 'rectangle') contexte = `Le triangle ABC est rectangle en ${NOMS[T.droit]}, et ${donnees}.`;
    else if (T.type.startsWith('isocele')) {
        const sommet = NOMS[[0, 1, 2].find((i) => T.egauxCotes.includes(i) && T.egauxCotes.includes((i + 2) % 3))];
        contexte = `Le triangle ABC est isocèle en ${sommet}, et ${donnees}.`;
    } else contexte = `Dans le triangle ABC, ${donnees}.`;
    const texte = `<p>${contexte}</p><p>Calcule la mesure de l'angle en ${NOMS[T.inconnu]}.</p>`;
    const payload = makeSaisie(String(reponse), texte, {
        ...opts(L_TROISIEME), suffixe: '°', elements_additionnels: imgElement(svg, 'medium'),
    });
    payload._meta.params = { type: T.type, angles: T.angles, inconnu: NOMS[T.inconnu], reponse };
    return payload;
};

// ─── Format 2 : possible ou pas ? (V/F) ────────────────────────────────────

const POOL_POSSIBLE = [
    { l: 'La somme des trois angles d\'un triangle vaut toujours 180°.', v: true },
    { l: 'Chaque angle d\'un triangle équilatéral mesure 60°.', v: true },
    { l: 'Dans un triangle rectangle, les deux angles non droits ont pour somme 90°.', v: true },
    { l: 'Un triangle isocèle peut avoir un angle de 120°.', v: true },
    { l: 'Un triangle peut avoir deux angles obtus.', v: false },
    { l: 'Un triangle peut avoir deux angles droits.', v: false },
    { l: 'Un triangle rectangle peut avoir un angle de 100°.', v: false },
    { l: 'Dans un triangle, si deux angles mesurent 60°, le troisième mesure 80°.', v: false },
    // Pool porté de 8 à 12 lignes (revue 2026-09-02) : fenêtre stricte des 4 dernières servies.
    { l: 'Un triangle peut avoir trois angles aigus.', v: true },
    { l: 'Dans un triangle isocèle, les deux angles à la base ont la même mesure.', v: true },
    { l: 'Un triangle peut avoir un angle droit et un angle obtus.', v: false },
    { l: 'Un triangle équilatéral peut avoir un angle de 90°.', v: false },
];

let derniersServisPossible1077 = [];

const genPossibleVf = () => {
    for (let essai = 0; essai < 60; essai++) {
        // Deux lignes NUMÉRIQUES recalculées : une réalisable, une impossible.
        const aV = randInt(25, 100); const bV = randInt(25, 155 - aV);       // somme ≤ 155 → réalisable
        const aF = randInt(60, 130); const bF = randInt(180 - aF, 120);      // somme ≥ 180 → impossible
        const numeriques = [
            { l: `Un triangle peut avoir des angles de ${aV}° et ${bV}°.`, v: aV + bV < 180 },
            { l: `Un triangle peut avoir des angles de ${aF}° et ${bF}°.`, v: aF + bF < 180 },
        ];
        if (numeriques[0].v !== true || numeriques[1].v !== false) throw new Error('gen_1077 : ligne numérique mal calibrée');
        // A8 (revue 2026-09-02) : la ligne numérique impossible ne doit pas être
        // déductible d'une ligne fixe du même tableau (deux obtus, deux droits).
        const exclues = [];
        if (aF > 90 && bF > 90) exclues.push('Un triangle peut avoir deux angles obtus.');
        if (aF === 90 && bF === 90) exclues.push('Un triangle peut avoir deux angles droits.');
        if ((aF === 90 && bF > 90) || (bF === 90 && aF > 90)) exclues.push('Un triangle peut avoir un angle droit et un angle obtus.');
        const dispo = POOL_POSSIBLE.filter((it) => !derniersServisPossible1077.includes(it.l) && !exclues.includes(it.l));
        const fixes = shuffle(dispo).slice(0, 2);
        const tirage = shuffle([...numeriques, ...fixes]);
        const nbFaux = tirage.filter((it) => !it.v).length;
        if (nbFaux < 1 || nbFaux > 3) continue;
        derniersServisPossible1077 = [...derniersServisPossible1077, ...fixes.map((it) => it.l)].slice(-4);
        const payload = makeVraiFaux(
            tirage.map((it) => it.l),
            tirage.map((it) => (it.v ? 0 : 1)),
            '<p>Pour chaque affirmation, dis si elle est vraie ou fausse.</p>',
            opts(L_POSSIBLE),
        );
        payload.contenu = { ...payload.contenu, cadence: 'raisonnement' };
        return payload;
    }
    throw new Error('gen_1077 : V/F sans mixité après 60 essais');
};

// ─── Registre ──────────────────────────────────────────────────────────────

const GENERATORS = {
    troisieme_saisie: genTroisieme,
    possible_tableau_vf: genPossibleVf,
};

const FORMAT_LABELS = { saisie: 'Saisie', tableau_vf: 'Vrai/Faux' };

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'troisieme', label: L_TROISIEME, formatsList: buildFormats('troisieme') },
    { id: 'possible', label: L_POSSIBLE, formatsList: buildFormats('possible') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
